import { PageHero, SectionLabel, BulletList, CTASection } from "@/components/Section";
import FramedPhoto from "@/components/FramedPhoto";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import {
  LayoutDashboard,
  Workflow,
  Sparkles,
  Compass,
  ShieldAlert,
  UserCog,
  Crown,
  Settings,
  Calculator,
  TrendingUp,
  Megaphone,
  ClipboardList,
  Receipt,
  HardHat,
  Users,
  BrainCircuit,
  Code2,
  Handshake,
  Shuffle,
  Target,
  Boxes,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";

const fractionalRoles = [
  { icon: Crown, label: "Fractional CEO", desc: "Executive planning, strategic direction and long-term structure." },
  { icon: Settings, label: "Fractional COO", desc: "Operational rhythm, systems, reporting and management framework." },
  { icon: Calculator, label: "Fractional CFO", desc: "Financial structure, forecasting, cashflow discipline and margin visibility." },
  { icon: Handshake, label: "Commercial Support", desc: "Pricing, contracts, claims and commercial discipline across the business." },
  { icon: TrendingUp, label: "Fractional Sales", desc: "Growth plans, sales processes and fractional sales teams that deliver pipeline." },
];

const partnerAreas = [
  { icon: HardHat, label: "Health & Safety" },
  { icon: Users, label: "HR" },
  { icon: Receipt, label: "Payroll" },
  { icon: Megaphone, label: "Marketing" },
  { icon: ClipboardList, label: "Quantity Surveying" },
  { icon: Calculator, label: "Accounting" },
];

const sections = [
  {
    icon: LayoutDashboard,
    eyebrow: "Systems & Structure",
    headline: "We build the operational backbone that allows your business to run properly.",
    items: [
      "Clear financial visibility",
      "Repeatable operational systems",
      "Reduced admin load",
      "Consistent reporting and governance",
    ],
  },
  {
    icon: Workflow,
    eyebrow: "Growth & Sales",
    headline: "We help businesses scale without chaos — with the plans, processes and sales capability to back it up.",
    items: [
      "A growth plan the whole team can execute",
      "Reliable sales pipeline and cadence",
      "Sales capacity without hiring full-time",
      "Margins that hold as you scale",
      "Clarity on the capacity you can take on",
    ],
  },
  {
    icon: Shuffle,
    eyebrow: "Restructuring & Right-sizing",
    headline: "We bring senior discipline to the hard calls — restructuring operations, right-sizing teams and recovering margin.",
    items: [
      "Recovered margin and cost discipline",
      "A right-sized team and cost base",
      "Cashflow stability under pressure",
      "The hard calls made with care",
      "A cleaner operational structure",
    ],
  },
  {
    icon: Target,
    eyebrow: "Exit Preparation",
    headline: "We structure businesses for a profitable mid-term exit — so when you're ready to sell, the business commands a premium.",
    items: [
      "Higher valuation at sale",
      "A business that runs without you",
      "Buyer-ready due diligence",
      "Clean, credible financials",
      "Confidence going into negotiation",
    ],
  },
  {
    icon: Handshake,
    eyebrow: "Commercial & Contracts",
    headline: "We bring senior commercial discipline to pricing, contracts and claims — the work that protects your margin.",
    items: [
      "Protected margins on every job",
      "Cleaner contracts and claims",
      "Pricing that reflects value",
      "Tenders won without eroding profit",
      "Fewer disputes and commercial surprises",
    ],
  },
  {
    icon: Compass,
    eyebrow: "Governance & Coaching",
    headline: "We bring structure to decision-making.",
    items: [
      "A leadership team that owns outcomes",
      "Better decision-making rhythm",
      "Accountability without micromanagement",
      "Confidence in the direction of the business",
    ],
  },
];

export default function WhatWeDo() {
  return (
    <div data-testid="what-we-do-page">
      <PageHero
        eyebrow="What We Do"
        title="Executive leadership. One relationship."
        intro="Backbench provides senior operational leadership directly. For specialist work, we coordinate a trusted network of partners — so you get every capability without managing multiple suppliers."
        testId="what-we-do-hero"
      />

      {/* MARQUEE — brand rhythm */}
      <Marquee
        testId="wwd-marquee"
        speed={50}
        items={[
          "SYSTEMS",
          "REPORTING",
          "SOFTWARE & TECH STACK",
          "COMMERCIAL & QS",
          "RESTRUCTURING",
          "EXIT PREPARATION",
          "GOVERNANCE",
          "HR · H&S · PAYROLL",
        ]}
      />

      <section className="bg-white" data-testid="what-we-do-sections">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32 space-y-20 lg:space-y-32">
          {sections.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.eyebrow}
                className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 pb-20 lg:pb-32 border-b border-[#E2E8F0] last:border-b-0 last:pb-0"
                data-testid={`practice-area-${i}`}
              >
                <div className="lg:col-span-5">
                  <div className="inline-flex items-center justify-center w-14 h-14 border border-[#0A192F] bg-white">
                    <Icon size={24} strokeWidth={1.5} className="text-[#0A192F]" />
                  </div>
                  <SectionLabel>{`0${i + 1} · ${s.eyebrow}`}</SectionLabel>
                  <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-[1.1] text-[#0A192F]">
                    {s.headline}
                  </h2>
                </div>
                <div className="lg:col-span-7 lg:pl-12 lg:border-l border-[#0A192F]/15">
                  <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#475569]">
                    You get
                  </div>
                  <div className="mt-6">
                    <BulletList items={s.items} testId={`practice-area-${i}-list`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FRACTIONAL ROLES */}
      <section
        className="bg-[#F8FAFC] border-t border-[#E2E8F0]"
        data-testid="fractional-roles-section"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <SectionLabel>Core Leadership</SectionLabel>
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
            <h2 className="lg:col-span-7 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] text-[#0A192F]">
              The senior seats we fill directly.
            </h2>
            <p className="lg:col-span-5 text-base lg:text-lg text-[#475569] leading-relaxed">
              Executive leadership provided directly by Backbench — embedded in your team, not sitting on the outside.
            </p>
          </div>

          <div className="mt-12 lg:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 border-t border-l border-[#E2E8F0]">
            {fractionalRoles.map((r, idx) => {
              const Icon = r.icon;
              return (
                <Reveal key={r.label} delay={(idx % 4) * 60}>
                  <div
                    className="bb-card bg-white border-r border-b border-[#E2E8F0] p-7 lg:p-8 flex flex-col gap-5 min-h-[240px] h-full"
                    data-testid={`fractional-role-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <Icon size={22} strokeWidth={1.5} className="text-[#0A192F]" />
                      <span className="font-mono text-[11px] tracking-widest text-[#F68712] font-bold">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <div className="mt-auto">
                      <h3 className="text-lg lg:text-xl font-extrabold tracking-tight text-[#0A192F]">
                        {r.label}
                      </h3>
                      <p className="mt-2 text-sm text-[#475569] leading-relaxed">
                        {r.desc}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <p className="mt-10 text-sm font-mono tracking-wide text-[#475569] max-w-3xl">
            Most engagements combine 2–3 seats. Common stacks: CFO + COO for reporting rhythm, CEO + CFO + Commercial for pre-sale structuring, or COO + Sales for growth plans.
          </p>
        </div>
      </section>

      {/* OTHER EXPERT SERVICES */}
      <section
        className="bg-[#0A192F] text-white border-t border-[#E2E8F0]"
        data-testid="partner-network-section"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <Reveal>
                <SectionLabel>Other Expert Services</SectionLabel>
                <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.02]">
                  One relationship.
                  <br />
                  <span className="text-[#F68712]">Every capability.</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:pl-12 lg:border-l border-white/15 space-y-5 text-base lg:text-lg text-white/80 leading-relaxed">
              <Reveal delay={120}>
                <p>
                  Backbench provides the executive leadership directly — CEO, COO, CFO, Commercial and Sales.
                </p>
                <p>
                  For the specialist disciplines that sit alongside — H&amp;S, HR, payroll, marketing, QS, accounting — we bring in other expert services that work inside our operating framework.
                </p>
                <p>
                  You get the right expertise for the right job — without having to source, manage or coordinate multiple providers yourself.
                </p>
              </Reveal>
            </div>
          </div>

          {/* Partner discipline grid */}
          <div className="mt-16 lg:mt-20 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 border-t border-l border-white/15">
            {partnerAreas.map((p, idx) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.label} delay={(idx % 4) * 60}>
                  <div
                    className="border-r border-b border-white/15 p-5 lg:p-6 flex flex-col gap-4 min-h-[140px] h-full"
                    data-testid={`partner-area-${idx}`}
                  >
                    <Icon size={22} strokeWidth={1.5} className="text-[#F68712]" />
                    <span className="mt-auto text-sm lg:text-base font-bold leading-tight text-white">
                      {p.label}
                    </span>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Three-line summary */}
          <div className="mt-16 lg:mt-20 grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-white/15">
            {[
              { label: "One", value: "relationship." },
              { label: "One", value: "operating framework." },
              { label: "One", value: "team focused on performance." },
            ].map((item, i) => (
              <div key={i} className="border-r border-b border-white/15 p-7 lg:p-9">
                <div className="font-mono text-[11px] tracking-widest text-[#F68712] font-bold">
                  0{i + 1}
                </div>
                <div className="mt-4">
                  <span className="text-2xl lg:text-3xl font-extrabold tracking-tight text-white">
                    {item.label}
                  </span>{" "}
                  <span className="text-2xl lg:text-3xl font-extrabold tracking-tight text-[#F68712]">
                    {item.value}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
